/* ==========================================================================
   SV Product Options — Seery-Us Visionz (replaces OPTIS)
   - Renders option sets from the "SV Option Set" metaobject
   - Conditional logic, required/min/max validation, per-value quantities
   - VIP-only options/values (locked teaser for everyone else), VIP pricing
   - Paid add-ons are added as nested cart lines under the product
   - Live price total + remembers a shopper's last choices
   Works on product pages and inside the collection quick-add modal.
   ========================================================================== */
(function () {
  if (window.SvOptions) return;

  const STORE_KEY = 'sv-opt-memory-v1';
  const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const norm = (s) => String(s == null ? '' : s).trim().toLowerCase();
  let uid = 0;
  const TRAY_TINTS = {
    'random mixed': null, 'pinks': ['#f4a6c8', '#1f2328'], 'purples': ['#a57ad8', '#ffffff'], 'blues': ['#5b9be6', '#ffffff'],
    'black': ['#2b2b2b', '#ffffff'], 'white': ['#f3f3f3', '#1f2328'], 'yellows': ['#f5d547', '#1f2328'], 'oranges': ['#f6a04d', '#1f2328'],
    'reds': ['#e25a5a', '#ffffff'], 'greens': ['#5cc28a', '#1f2328']
  };
  // VIP color pickers always include the silk filaments
  const SILKS = [
    { label: 'Silk White', image: 'https://cdn.shopify.com/s/files/1/0511/5428/8799/files/sv-sw-sv-silk-white.png' },
    { label: 'Silk Chrome', image: 'https://cdn.shopify.com/s/files/1/0511/5428/8799/files/sv-sw-sv-silk-chrome.png' },
    { label: 'Silk Teal', image: 'https://cdn.shopify.com/s/files/1/0511/5428/8799/files/sv-sw-sv-silk-teal.png' },
    { label: 'Silk Purple', image: 'https://cdn.shopify.com/s/files/1/0511/5428/8799/files/sv-sw-sv-silk-purple.png' },
    { label: 'Silk Magenta', image: 'https://cdn.shopify.com/s/files/1/0511/5428/8799/files/sv-sw-sv-silk-magenta.png' },
    { label: 'Silk Blue', image: 'https://cdn.shopify.com/s/files/1/0511/5428/8799/files/sv-sw-sv-silk-blue.png' },
    { label: 'Silk Gunmetal Grey', image: 'https://cdn.shopify.com/s/files/1/0511/5428/8799/files/sv-sw-sv-silk-gunmetal-grey.png' },
    { label: 'Silk Red', image: 'https://cdn.shopify.com/s/files/1/0511/5428/8799/files/sv-sw-sv-silk-red.png' },
    { label: 'Silk Green', image: 'https://cdn.shopify.com/s/files/1/0511/5428/8799/files/sv-sw-sv-silk-green.png' }
  ];
  const withSilks = (vals) => vals.concat(SILKS.filter((sk) => !vals.some((v) => v.label === sk.label)).map((v) => Object.assign({}, v)));
  const trayDot = (name) => {
    const t = TRAY_TINTS[norm(name)];
    return t ? 'background:' + t[0] : 'background:conic-gradient(#f4a6c8,#f5d547,#5cc28a,#5b9be6,#a57ad8,#f4a6c8)';
  };

  function readMemory() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY) || '{}') || {}; } catch (e) { return {}; }
  }
  function writeMemory(mem) {
    try { localStorage.setItem(STORE_KEY, JSON.stringify(mem)); } catch (e) { /* storage blocked */ }
  }

  function formatMoney(cents, format) {
    const fmt = format || '${{amount}}';
    const n = (Number(cents) || 0) / 100;
    const withDelims = (num, dec, thou, decSep) => {
      const parts = num.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return parts.join(decSep);
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelims(n, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(n, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(n, 0, '.', ',');
        default: return withDelims(n, 2, ',', '.');
      }
    });
  }

  class SvOptionsElement extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      const cfgEl = this.querySelector('script[data-sv-config]');
      if (!cfgEl) return;
      try { this.cfg = JSON.parse(cfgEl.textContent); } catch (e) { console.error('[SV Options] bad config', e); return; }
      this._ready = true;
      this.uid = 'sv' + (++uid);
      this.body = this.querySelector('[data-sv-body]');
      this.totalEl = this.querySelector('[data-sv-total]');
      this.errorEl = this.querySelector('[data-sv-error]');
      const lib = this.cfg.library || {};
      this.options = (this.cfg.options || []).map((o, i) => {
        const opt = Object.assign({ id: 'o' + i }, o);
        if (opt.values_from && !opt.values && lib.palettes) opt.values = (lib.palettes[opt.values_from] || []).map((v) => Object.assign({}, v));
        if (opt.html_from && !opt.html && lib.blocks) opt.html = lib.blocks[opt.html_from] || '';
        return opt;
      });
      // Options authored `multi: false` are rendered as chips (so they flip to multi for markup),
      // but they stay SINGLE-PICK: the set author's intent wins. An option that genuinely wants
      // several picks must say `multi: true` in the option set.
      // Exception: `qty` options (pick several, each with its own quantity) keep multi-select.
      // (medallion listings skip the flip entirely and render as authored)
      const keepSingle = /medallion/i.test(this.cfg.set || '');
      this.options.forEach((o) => { if ((o.type === 'choice' || o.type === 'swatch') && !o.multi && !keepSingle && !o.force_dropdown) { o.multi = true; if (o.single || !o.qty) o.one = true; } });
      // "Customize Your Lid?", "Font" and options flagged "one" are pick-one everywhere (tap again to clear)
      // "many": true on an option opts out of the pick-one rules below (e.g. "Build A Set" with quantities)
      this.options.forEach((o) => { if (((o.one || (o.one_nonvip && !this.isVip()) || /customi[sz]e your lid|thickness|\bholes?\b|\bsets?\b|acrylic colou?r|colou?r of acrylic/i.test(o.label || '') || (o.values || []).some((v) => /case only/i.test(v.label || '')) || /^font( choices?)?$/i.test((o.label || '').trim()))) && o.multi && !o.many) o._one = true; });
      // VIP perk: every paid "Customize Your Lid" upgrade is free for VIP members on every listing
      this.options.forEach((o) => { if (/customi[sz]e your lid/i.test(o.label || '')) (o.values || []).forEach((v) => { if (v.variant) v.vip_free = true; }); });
      this.setupTray();
      this.setupMockup();
      this.setupVariantProxy();
      this.setupVipColors();
      // badge buddies / badge toppers also get the thickness choice
      if (/badge (buddy|buddies|topper)/i.test(this.cfg.productTitle || '')) this.ensureThickness();
      this.state = {}; // optId -> { values: [labels], qty: {label: n}, text: '' }
      this.render();
      this.applyMemory();
      this.applyDefaults();
      this.refresh();
      if (this.cfg.tray && this.cfg.tray.system) { this.hideNativePickers(); this.renderTrayPreview(); }
      this.bindVariantWatch();
      // Shopify's quantity box goes under the options, right above the add button
      const qtyBox = this.scopeEl().querySelector('.product-form__quantity');
      if (qtyBox && !this.contains(qtyBox) && this.nextElementSibling !== qtyBox) { this.after(qtyBox); qtyBox.classList.add('sv-qty-moved'); }
      if (this.cfg.vipOnly && !this.cfg.isVip) this.lockPurchase();
      // Buy it now skips SV Options: file uploads arrive as just a file name (C:\fakepath\...), paid add-ons are never added
      // and required choices are never checked. Every SV Options listing checks out through Add to Cart only.
      { const pf = this.form; if (pf) pf.classList.add('sv-no-dynamic'); }
    }

    /* ---------- helpers ---------- */
    get form() {
      const byId = document.getElementById(this.dataset.form);
      if (byId) return byId;
      const scope = this.closest('.product__info-container, .quick-add-modal__content-info, section') || document;
      return scope.querySelector('product-form form');
    }
    isVip() { return !!this.cfg.isVip; }
    // Tier 2. Every Visionary is also a VIP (their tags are in both lists),
    // so a Visionary sees VIP options AND audience:"visionaries" options.
    // A plain VIP is not a Visionary, so tier-2 options stay locked for them.
    isVisionary() { return !!this.cfg.isVisionary; }
    audienceOk(a) {
      if (!a || a === 'all') return 'show';
      if (a === 'vip') return this.isVip() ? 'show' : 'locked';
      if (a === 'visionaries' || a === 'visionary') return this.isVisionary() ? 'show' : 'locked';
      if (a === 'regular') return this.isVip() ? 'hide' : 'show';
      return 'show';
    }
    propName(opt) { return 'properties[' + opt.label + ']'; }
    lockHtml() {
      return '<a class="sv-lock" href="' + esc(this.cfg.vipUrl) + '" target="_blank" rel="noopener">🔒 ' + esc(this.cfg.vipLabel) + '</a>';
    }
    addonPrice(variantId) {
      const a = variantId && this.cfg.addons ? this.cfg.addons[String(variantId)] : null;
      return a ? a.price : null;
    }
    valuePriceInfo(val) {
      // returns { variant, handle, price } that applies for this shopper, or null for free
      if (this.isVip()) {
        if (val.vip_free) return null;
        if (val.vip_variant) return { variant: val.vip_variant, price: this.addonPrice(val.vip_variant) };
      }
      if (val.variant) return { variant: val.variant, price: this.addonPrice(val.variant) };
      return null;
    }
    optionPriceInfo(opt) {
      if (this.isVip() && opt.vip_free) return null;
      if (opt.variant) return { variant: opt.variant, price: this.addonPrice(opt.variant) };
      return null;
    }
    priceTag(info, vipNote) {
      // vipNote = this add-on is a free upgrade for VIP members
      if (!info || info.price == null) return vipNote ? '<span class="sv-price sv-price--vip">\u2714 Free Upgrade VIP</span>' : '';
      return '<span class="sv-price">+' + esc(formatMoney(info.price, this.cfg.moneyFormat)) + '</span>' +
        (vipNote ? ' <a class="sv-lock sv-lock--free" href="' + esc(this.cfg.vipUrl) + '" target="_blank" rel="noopener">\uD83D\uDD12 Free Upgrade VIP</a>' : '');
    }

    /* ---------- tray builder (visual insert picker) ---------- */
    setupTray() {
      const t = this.cfg.tray;
      if (!t || !t.system) return;
      const drop = (t.replaces || []).map(norm);
      let at = -1;
      this.options = this.options.filter((o, i) => {
        const gone = drop.includes(norm(o.label));
        if (gone && at < 0) at = i;
        return !gone;
      });
      const opt = { id: 'tb', type: 'tray_builder', label: t.label || 'Build your tray', required: t.pricing === 'included' };
      // VIP perk: exact filament colors (Color 1 + Color 2) inside the tray section
      const pal = t.vip_palette === false ? null : (((this.cfg.library || {}).palettes || {})[t.vip_palette || 'p37-0']);
      if (t.colorOption && pal && pal.length) {
        const pv = () => withSilks(pal.map((v) => Object.assign({}, v)));
        const sw = (id, label, extra) => Object.assign({ id, type: 'swatch', label, display: 'swatch', multi: false, audience: 'vip', _inCard: true,
          values: pv() }, extra || {});
        if (this.isVip()) {
          this.options.push(sw('vipc', 'VIP Color Picker', { multi: true, max: 2 }));
        } else {
          this.options.push(sw('vipc', 'VIP Color Picker', { values: [],
            lockNote: withSilks(pal).length + ' exact filament colors incl. silks (pick up to 2) unlock for VIP members.' }));
        }
      }
      const inCard = (t.card_options || []).map(norm);
      this.options.forEach((o) => { if (inCard.includes(norm(o.label))) o._inCard = true; });
      this.options.unshift(opt);
      // same layout on every tray listing: tray card, then add-ons (storage, lid + its details), notes, must read
      const byId = {}; this.options.forEach((o) => { byId[o.id] = o; });
      const lid = this.options.find((o) => /customi[sz]e your lid/i.test(o.label || ''));
      const rank = (o) => {
        if (o.type === 'tray_builder') return 0;
        const l = norm(o.label);
        if (/^must read/.test(l)) return 90;
        if (/^notes/.test(l)) return 80;
        if (lid && o === lid) return 30;
        if (lid && (o.show_if || []).some((r) => r.option === lid.id)) return 31;
        if (lid && o.type === 'info' && /instruction/.test(l) && !(o.show_if || []).length) return 31;
        return 20;
      };
      if (lid) this.options.forEach((o) => {
        if (o.type === 'info' && /instruction/i.test(o.label || '') && !(o.show_if || []).length && rank(o) === 31) o.show_if = [{ option: lid.id, value: 'any' }];
      });
      this.options = this.options.map((o, i) => ({ o, i })).sort((a, b) => (rank(a.o) - rank(b.o)) || (a.i - b.i)).map((x) => x.o);
      // tray-extras variants -> lookup + price/availability for totals & validation
      this.trayVariants = {};
      this.cfg.addons = this.cfg.addons || {};
      (t.variants || []).forEach((v) => {
        this.trayVariants[norm(v.o1) + '|' + norm(v.o2)] = v;
        this.cfg.addons[String(v.id)] = { price: v.price, available: v.available, title: v.o1 + ' / ' + v.o2 };
      });
      if (t.setVariant) this.cfg.addons[String(t.setVariant.id)] = { price: t.setVariant.price, available: t.setVariant.available, title: t.setVariant.title };
      this.trayState = { rows: [], extras: [], inserts: 0, complete: false, layout: '', extrasText: '' };
    }
    scopeEl() { return this.closest('.product__info-container, .quick-add-modal__content-info, section') || document; }
    /* ---------- acrylic thickness comparison popup (drawn to scale) ---------- */
    thicknessRows(opt) {
      // same names everywhere on the site: Regular 1/8", Thin 1/16", Ultra Thin 1/32"
      const medal = /medallion/i.test((this.cfg.set || '') + ' ' + (this.cfg.productTitle || ''));
      const T = {
        '1/8': { mm: 3.18, name: 'Regular 1/8"', note: medal ? 'Our standard. Sturdy & rigid.' : 'Our standard. Still slim, but sturdy. Best for keychains, charms, badge reels & signs.' },
        '1/16': { mm: 1.59, name: 'Thin 1/16"', note: medal ? 'Sits flat under Tipsy Magnolia plumps. Perfect for: Hats • Apparel • Tumblers • Bags • Handmade Goods • Pet Accessories • Home Décor • Craft Products • Packaging • Custom Projects' :
          'Half as thick. Lighter & a little flexible. Great for layering.' },
        '1/32': { mm: 0.79, name: 'Ultra Thin 1/32"', note: 'About a credit card. Sits flat on all tumbler bottoms. Not suitable for clothing, hats, etc.' }
      };
      const key = (label) => {
        const l = norm(label);
        if (/ultra/.test(l) || /1\/32/.test(l)) return '1/32';
        if (/1\/16/.test(l) || /\bthin\b/.test(l)) return '1/16';
        if (/1\/8/.test(l) || /regular/.test(l)) return '1/8';
        return null;
      };
      this._thickMedal = medal;
      const seen = {};
      return (opt.values || []).map((v) => {
        const k = key(v.label);
        if (!k || seen[k]) return null;
        seen[k] = 1;
        return Object.assign({ label: v.label }, T[k]);
      }).filter(Boolean);
    }
    thicknessSvg(rows) {
      // side view of a 2" piece, length and thickness at the SAME scale, so it looks as slim as it really is
      const W = 300, pxmm = W / 50.8;
      const cmp = { '1/8': '≈ 2 quarters', '1/16': '≈ 1 quarter', '1/32': '≈ a credit card' };
      let y = 8;
      const parts = rows.map((r) => {
        const h = Math.max(2, r.mm * pxmm);
        const k = r.mm > 3 ? '1/8' : (r.mm > 1.2 ? '1/16' : '1/32');
        const top = y + 16; y = top + h + 22;
        return '<text x="0" y="' + (top - 5) + '" font-size="12.5" font-weight="700" fill="#1f2328">' + esc(r.name) +
          '<tspan font-weight="400" font-size="11" fill="#6b7280"> · ' + r.mm.toFixed(1) + ' mm ' + cmp[k] + '</tspan></text>' +
          '<rect x="0" y="' + top + '" width="' + W + '" height="' + h.toFixed(1) + '" rx="1" fill="url(#svtAcr)" stroke="#6fb6cf" stroke-width=".8"/>';
      }).join('');
      const qd = 24.26 * pxmm, qh = 1.75 * pxmm, qt = y + 16;
      y = qt + qh + 10;
      const quarter = '<text x="0" y="' + (qt - 5) + '" font-size="12.5" font-weight="700" fill="#1f2328">US quarter<tspan font-weight="400" fill="#6b7280"> · for size</tspan></text>' +
        '<rect x="0" y="' + qt + '" width="' + qd.toFixed(1) + '" height="' + qh.toFixed(1) + '" rx="' + (qh / 2).toFixed(1) + '" fill="url(#svtCoin)" stroke="#8a8f96" stroke-width=".8"/>';
      const ruler = '<line x1="0" x2="' + W + '" y1="' + (y + 6) + '" y2="' + (y + 6) + '" stroke="#9ca3af"/><line x1="0" x2="0" y1="' + (y + 2) + '" y2="' + (y + 10) + '" stroke="#9ca3af"/>' +
        '<line x1="' + W + '" x2="' + W + '" y1="' + (y + 2) + '" y2="' + (y + 10) + '" stroke="#9ca3af"/>' +
        '<text x="' + (W / 2) + '" y="' + (y + 22) + '" font-size="11" text-anchor="middle" fill="#6b7280">' + (this._thickMedal ? '2" medallion' : '2" piece') + ', side view</text>';
      return '<svg viewBox="0 -2 ' + W + ' ' + (y + 30) + '" width="100%" role="img" aria-label="Side view of a 2 inch piece in each thickness, drawn to scale next to a US quarter">' +
        '<defs><linearGradient id="svtAcr" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#e6f6fb"/><stop offset="1" stop-color="#8fcbe0"/></linearGradient>' +
        '<linearGradient id="svtCoin" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#e3e5e8"/><stop offset="1" stop-color="#a9adb3"/></linearGradient></defs>' +
        parts + quarter + ruler + '</svg>';
    }
    openThickness(optId) {
      const opt = this.optById(optId);
      if (!opt) return;
      const rows = this.thicknessRows(opt);
      let dlg = this.querySelector('dialog.sv-thick');
      if (!dlg) {
        dlg = document.createElement('dialog');
        dlg.className = 'sv-thick';
        dlg.addEventListener('click', (e) => { if (e.target === dlg || e.target.closest('[data-sv-thick-close]')) dlg.close(); });
        dlg.addEventListener('keyup', (e) => { if (e.key === 'Escape') e.stopPropagation(); });
        this.appendChild(dlg);
      }
      dlg.innerHTML = '<div class="sv-thick__box">' +
        '<div class="sv-thick__head"><strong>Acrylic thickness, side view</strong><button type="button" class="sv-thick__x" data-sv-thick-close aria-label="Close">×</button></div>' +
        '<p class="sv-thick__sub">True to scale: a 2" ' + (this._thickMedal ? 'medallion' : 'piece') + ' seen from the side, next to a quarter.</p>' + this.thicknessSvg(rows) +
        '<ul class="sv-thick__list">' + rows.map((r) => '<li><strong>' + esc(r.name) + '</strong> – ' + esc(r.note) + '</li>').join('') + '</ul>' +
        '<button type="button" class="sv-thick__ok" data-sv-thick-close>Got it</button></div>';
      if (dlg.showModal) dlg.showModal(); else dlg.setAttribute('open', '');
    }
    /* ---------- Shopify variant options drawn in the SV style (variants stay behind the scenes) ---------- */
    setupVariantProxy() {
      this.pv = [];
      const picker = this.scopeEl().querySelector('variant-radios, variant-selects');
      if (!picker) return;
      let variants = [];
      try { variants = JSON.parse(picker.querySelector('script[type="application/json"]').textContent) || []; } catch (e) { variants = []; }
      const groups = [];
      picker.querySelectorAll('select').forEach((sel) => {
        const wrap = sel.closest('.product-form__input') || sel.parentElement;
        const lab = wrap.querySelector('label');
        groups.push({ name: (lab ? lab.textContent : sel.name.replace(/^options\[|\]$/g, '')).trim(), sel, wrap, values: Array.from(sel.options).map((o) => o.value) });
      });
      picker.querySelectorAll('fieldset').forEach((fs) => {
        const inputs = Array.from(fs.querySelectorAll('input[type="radio"]'));
        if (!inputs.length) return;
        const lg = fs.querySelector('legend');
        groups.push({ name: (lg ? lg.textContent : inputs[0].name).trim(), inputs, wrap: fs, values: inputs.map((i) => i.value) });
      });
      if (!groups.length) return;
      groups.forEach((g, i) => { g.pos = i; g.wrap.classList.add('sv-native-hidden'); });
      picker.classList.add('sv-native-picker');
      const trayColor = this.cfg.tray && this.cfg.tray.system && this.cfg.tray.colorOption ? norm(this.cfg.tray.colorOption.name) : null;
      this.pvVariants = variants;
      this.pv = groups.filter((g) => norm(g.name) !== trayColor);
      // never pre-pick a size: every Size option starts empty until the shopper chooses one
      this.pvUnset = {};
      this.pv.forEach((g) => { if (/\bsizes?\b/i.test(g.name)) this.pvUnset[g.pos] = true; });
      // acrylic listings: pick several sizes of the same design, each with its own quantity
      const sizeG = this.pv.find((g) => /^sizes?$/i.test(g.name));
      if (sizeG && groups.some((g) => /acrylic|finish/i.test(g.name)) && !(this.cfg.tray && this.cfg.tray.system)) {
        this.pvMultiPos = sizeG.pos;
        this.pvSizes = {};
        delete this.pvUnset[sizeG.pos];
        this.scopeEl().querySelectorAll('.product-form__quantity').forEach((el) => { el.style.display = 'none'; });
        // Shopify caps products at 3 options, so acrylic thickness lives here instead of as a variant
        // not on shaker or SVG listings (and hidden whenever a Shaker finish is picked)
        if (!/shaker|svg/i.test(this.cfg.productTitle || '')) this.ensureThickness();
      }
    }
    // every 3D print (native Color option in color families) gets the VIP exact-color picker
    setupVipColors() {
      // a set can place the picker itself with { "type": "vip_picker", "extra_values": [...] }
      const phIdx = this.options.findIndex((o) => o.type === 'vip_picker');
      const ph = phIdx >= 0 ? this.options[phIdx] : null;
      if (ph) this.options.splice(phIdx, 1);
      if (this.options.some((o) => o.id === 'vipc')) return;
      const fam = /^(random mixed|pinks|purples|blues|yellows|oranges|reds|greens)$/i;
      const colorG = (this.pv || []).find((g) => /^colou?rs?$/i.test(g.name) && g.values.filter((v) => fam.test(v)).length >= 4);
      if (!colorG && !ph) return;
      const pal = ((this.cfg.library || {}).palettes || {})['p37-0'];
      if (!pal || !pal.length) return;
      const vals = withSilks(pal.map((v) => Object.assign({}, v)).concat(((ph && ph.extra_values) || []).map((v) => Object.assign({}, v))));
      const base = { id: 'vipc', type: 'swatch', label: (ph && ph.label) || 'VIP Color Picker', display: 'swatch', audience: 'vip', help: (ph && ph.help) || (/tray/i.test(this.cfg.productTitle || '') ? 'Silks work best for functionality of trays for resin & diamond painting' : '') };
      const opt = this.isVip()
        ? Object.assign(base, { multi: true, max: 2, values: vals })
        : Object.assign(base, { multi: false, values: [], lockNote: vals.length + ' exact filament colors incl. silks (pick up to 2) unlock for VIP members.' });
      if (ph) this.options.splice(phIdx, 0, opt); else this.options.unshift(opt);
    }
    ensureThickness() {
      if (this.options.some((o) => /thickness/i.test(o.label || ''))) return;
      this.options.unshift({ id: 'thick', type: 'choice', label: 'Acrylic Thickness', display: 'chips', multi: true, one: true, _one: true, required: true,
        default: 'Regular 1/8"', values: [{ label: 'Regular 1/8"' }, { label: 'Thin 1/16"' }] });
    }
    pvNativeValues() {
      const out = [];
      const picker = this.scopeEl().querySelector('variant-radios, variant-selects');
      if (!picker) return out;
      picker.querySelectorAll('select').forEach((s) => out.push(s.value));
      picker.querySelectorAll('fieldset').forEach((fs) => {
        if (!fs.querySelector('input[type="radio"]')) return;
        const c = fs.querySelector('input[type="radio"]:checked'); out.push(c ? c.value : '');
      });
      return out;
    }
    pvSizeItems() {
      if (this.pvMultiPos == null) return [];
      const g = this.pv.find((x) => x.pos === this.pvMultiPos);
      const base = this.pvNativeValues();
      return g.values.filter((v) => this.pvSizes[v]).map((v) => {
        const want = base.slice(); want[this.pvMultiPos] = v;
        const vr = (this.pvVariants || []).find((x) => (x.options || []).every((o, i) => want[i] === o));
        const ok = vr && vr.available !== false;
        return { value: v, qty: this.pvSizes[v], variantId: ok ? vr.id : null, price: vr ? vr.price : 0, missing: !vr, soldOut: vr && vr.available === false };
      });
    }
    pvSetSize(value, on, qty) {
      if (on) this.pvSizes[value] = Math.max(1, qty || this.pvSizes[value] || 1); else delete this.pvSizes[value];
      const g = this.pv.find((x) => x.pos === this.pvMultiPos);
      const first = g.values.find((v) => this.pvSizes[v]);
      if (first && first !== this.pvCurrent(g)) this.pickVariant(this.pvMultiPos, first); else this.syncVariantProxy();
      this.renderTotal();
    }
    pvCurrent(g) {
      if (g.sel) return g.sel.value;
      const c = g.inputs.find((i) => i.checked);
      return c ? c.value : '';
    }
    pvShown(g) {
      return this.pvUnset && this.pvUnset[g.pos] ? '' : this.pvCurrent(g);
    }
    pvImage(label) {
      const pal = ((this.cfg.library || {}).palettes || {})['p37-0'] || [];
      const hit = pal.find((v) => v.image && norm(v.label) === norm(label));
      return hit ? hit.image : '';
    }
    renderVariantProxy() {
      if (!this.pv || !this.pv.length) return '';
      return '<div class="sv-varpick" data-sv-varpick>' + this.pv.map((g) => {
        const id = this.uid + '-var' + g.pos;
        const isColor = /colou?r/i.test(g.name);
        const cur = this.pvShown(g);
        const multi = g.pos === this.pvMultiPos;
        const cls = isColor ? 'sv-values sv-values--swatch sv-values--varcolor' : 'sv-values sv-values--chips' + (g.values.length > 5 ? ' sv-values--grid2' : '');
        if (!isColor && !multi && /^style\s*\/\s*size$/i.test(g.name.trim())) {
          return '<div class="sv-opt sv-opt--variant" data-sv-var="' + g.pos + '">' +
            '<label class="sv-label" for="' + id + '" id="' + id + '-l">' + esc(g.name) + ' <span class="sv-var-cur" data-sv-var-cur>' + esc(cur) + '</span></label>' +
            '<div class="select"><select class="sv-select select__select" id="' + id + '" data-sv-varselect="' + g.pos + '">' +
            (cur === '' ? '<option value="" selected disabled>Choose ' + esc(g.name.toLowerCase()) + '</option>' : '') +
            g.values.map((v) => '<option value="' + esc(v) + '"' + (v === cur ? ' selected' : '') + '>' + esc(v) + '</option>').join('') +
            '</select></div></div>';
        }
        return '<div class="sv-opt sv-opt--variant" data-sv-var="' + g.pos + '">' +
          '<div class="sv-label" id="' + id + '-l">' + esc(g.name) + ' <span class="sv-var-cur" data-sv-var-cur>' + esc(cur) + '</span></div>' +
          (multi ? '<div class="sv-help sv-var-hint">Pick one or more sizes and set how many of each.</div>' +
            (this.pv.some((x) => x.values.some((v) => /shaker/i.test(v))) ?
              '<div class="sv-var-note"><strong>Add Shakers Separately</strong> as only available in 2" and above.</div>' : '') : '') +
          '<div class="' + cls + '" role="radiogroup" aria-labelledby="' + id + '-l">' +
          g.values.map((v, i) => {
            const vid = id + '-' + i;
            let media = '';
            if (isColor) {
              const img = this.pvImage(v);
              media = img ? '<img src="' + esc(img) + '" alt="" loading="lazy" width="44" height="44">' :
                (TRAY_TINTS.hasOwnProperty(norm(v)) || /random/i.test(v) ? '<span class="sv-dot" style="' + trayDot(v) + '"></span>' : '');
            }
            if (multi) {
              const q = this.pvSizes[v];
              return '<label class="sv-value" for="' + vid + '" data-sv-var-val="' + esc(v) + '">' +
                '<input type="checkbox" id="' + vid + '" value="' + esc(v) + '" data-sv-varsize' + (q ? ' checked' : '') + '>' +
                '<span class="sv-value__inner"><span class="sv-value__text">' + esc(v) + '</span></span>' +
                '<span class="sv-qty" data-sv-varqty-wrap' + (q ? '' : ' hidden') + '>' +
                '<button type="button" data-sv-varstep="-1" aria-label="Fewer ' + esc(v) + '">−</button>' +
                '<input type="number" min="1" value="' + (q || 1) + '" data-sv-varqty="' + esc(v) + '" aria-label="How many ' + esc(v) + '">' +
                '<button type="button" data-sv-varstep="1" aria-label="More ' + esc(v) + '">+</button></span></label>';
            }
            return '<label class="sv-value" for="' + vid + '" data-sv-var-val="' + esc(v) + '">' +
              '<input type="radio" id="' + vid + '" name="' + id + '" value="' + esc(v) + '" data-sv-varchoice="' + g.pos + '"' + (v === cur ? ' checked' : '') + '>' +
              '<span class="sv-value__inner">' + media + '<span class="sv-value__text">' + esc(v) + '</span></span></label>';
          }).join('') + '</div></div>';
      }).join('') + '</div>';
    }
    pickVariant(pos, value) {
      const g = (this.pv || []).find((x) => x.pos === pos);
      if (!g) return;
      if (this.pvUnset) delete this.pvUnset[pos];
      if (g.sel) { g.sel.value = value; g.sel.dispatchEvent(new Event('change', { bubbles: true })); }
      else {
        const r = g.inputs.find((i) => i.value === value);
        if (r) { r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })); }
      }
      this.syncVariantProxy();
    }
    syncVariantProxy() {
      if (!this.pv || !this.pv.length || !this.body) return;
      const picker = this.scopeEl().querySelector('variant-radios, variant-selects');
      const groupsAll = [];
      if (picker) {
        picker.querySelectorAll('select').forEach((s) => groupsAll.push(s.value));
        picker.querySelectorAll('fieldset').forEach((fs) => { const c = fs.querySelector('input[type="radio"]:checked'); if (fs.querySelector('input[type="radio"]')) groupsAll.push(c ? c.value : ''); });
      }
      // a size the shopper hasn't picked yet shouldn't grey out other choices
      Object.keys(this.pvUnset || {}).forEach((p) => { groupsAll[Number(p)] = undefined; });
      if (this.pvMultiPos != null && !Object.keys(this.pvSizes || {}).length) groupsAll[this.pvMultiPos] = undefined;
      const variants = this.pvVariants || [];
      this.pv.forEach((g) => {
        const el = this.body.querySelector('[data-sv-var="' + g.pos + '"]');
        if (!el) return;
        const cur = this.pvShown(g);
        const curEl = el.querySelector('[data-sv-var-cur]');
        if (g.pos === this.pvMultiPos) {
          const picked = g.values.filter((v) => this.pvSizes[v]);
          if (curEl) curEl.textContent = picked.map((v) => this.pvSizes[v] > 1 ? v + ' ×' + this.pvSizes[v] : v).join(', ');
          el.querySelectorAll('[data-sv-varsize]').forEach((inp) => {
            inp.checked = !!this.pvSizes[inp.value];
            const w = inp.closest('.sv-value').querySelector('[data-sv-varqty-wrap]');
            if (w) w.hidden = !inp.checked;
          });
        } else if (curEl) curEl.textContent = cur;
        el.querySelectorAll('[data-sv-varchoice]').forEach((inp) => { inp.checked = inp.value === cur; });
        el.querySelectorAll('[data-sv-varselect]').forEach((sel) => {
          sel.value = cur;
          Array.from(sel.options).forEach((op) => {
            if (!op.value) return;
            const want = groupsAll.slice(); want[g.pos] = op.value;
            const match = variants.find((vr) => (vr.options || []).every((o, i) => want[i] === undefined || want[i] === o));
            op.textContent = op.value + (!match ? ' (unavailable)' : (!match.available ? ' (sold out)' : ''));
          });
        });
        el.querySelectorAll('[data-sv-var-val]').forEach((lab) => {
          const v = lab.dataset.svVarVal;
          const want = groupsAll.slice(); want[g.pos] = v;
          const match = variants.find((vr) => (vr.options || []).every((o, i) => want[i] === undefined || want[i] === o));
          lab.classList.toggle('sv-value--soldout', !match || !match.available);
          lab.title = !match ? 'Not available in this combination' : (!match.available ? 'Sold out' : '');
        });
      });
    }
    hideNativePickers() {
      const t = this.cfg.tray, scope = this.scopeEl();
      if (t.colorOption) {
        const cname = t.colorOption.name;
        scope.querySelectorAll('variant-selects, variant-radios').forEach((picker) => {
          picker.querySelectorAll('select').forEach((sel) => {
            if (sel.name === 'options[' + cname + ']') { const w = sel.closest('.product-form__input') || sel.parentElement; w.style.display = 'none'; }
          });
          picker.querySelectorAll('fieldset').forEach((fs) => {
            if (fs.querySelector('input[name="' + cname.replace(/"/g, '') + '"]')) fs.style.display = 'none';
          });
          if (t.optionCount === 1) { picker.style.display = 'none'; }
        });
      }
      scope.querySelectorAll('.product-form__quantity').forEach((el) => { el.style.display = 'none'; });
    }
    trayColorChips() {
      const t = this.cfg.tray;
      if (!t.colorOption) return '';
      const cur = norm(this.trayColor());
      return '<div class="sv-tb-colors" role="radiogroup" aria-label="' + esc(t.colorOption.name) + '">' +
        t.colorOption.values.map((v) => '<button type="button" class="sv-tb-color' + (norm(v) === cur ? ' is-on' : '') + '" role="radio" aria-checked="' + (norm(v) === cur) +
          '" data-svtb-color="' + esc(v) + '"><span class="sv-tb-color__dot" style="' + trayDot(v) + '"></span>' + esc(v) + '</button>').join('') + '</div>';
    }
    setTrayColor(val) {
      const scope = this.scopeEl();
      let done = false;
      scope.querySelectorAll('variant-selects select, variant-radios select').forEach((sel) => {
        if (done) return;
        const o = Array.from(sel.options).find((x) => x.value === val);
        if (o) { sel.value = val; sel.dispatchEvent(new Event('change', { bubbles: true })); done = true; }
      });
      if (!done) {
        const r = Array.from(scope.querySelectorAll('variant-radios input[type="radio"], variant-selects input[type="radio"]')).find((x) => x.value === val);
        if (r) { r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })); done = true; }
      }
      // reflect immediately, then again once the theme has swapped the variant id
      this._pendingColor = val;
      const redraw = () => {
        this.querySelectorAll('[data-svtb-colors-wrap]').forEach((w) => { w.innerHTML = this.trayColorChips(); });
        this.refresh(); this.renderTrayPreview();
        const dlg = this.querySelector('dialog.sv-tb-dialog');
        if (dlg && dlg.open) window.SvTrayBuilder.create(dlg.querySelector('[data-svtb-builder]'), this.trayBuilderOpts({ onChange: (st) => { this.trayState = st; this.refresh(); this.renderTrayPreview(); } }));
      };
      redraw(); setTimeout(() => { this._pendingColor = null; redraw(); }, 400);
    }
    trayTint() {
      const t = TRAY_TINTS[norm(this.trayColor())];
      return t ? { bg: t[0], fg: t[1] } : null;
    }
    stepTrayQty(delta, abs) {
      const f = this.form;
      const q = f && (document.querySelector('input[name="quantity"][form="' + f.getAttribute('id') + '"]') || f.querySelector('input[name="quantity"]'));
      const cur = Math.max(1, parseInt(q ? q.value : 1, 10) || 1);
      const next = Math.max(1, abs != null ? abs : cur + delta);
      if (q) { q.value = next; q.dispatchEvent(new Event('change', { bubbles: true })); }
      this.querySelectorAll('[data-svtb-qty]').forEach((i) => { i.value = next; });
      this.renderTotal();
    }
    trayColor() {
      if (this._pendingColor) return this._pendingColor;
      const t = this.cfg.tray || {};
      const vid = this.currentVariantId();
      return (t.colors && vid && t.colors[String(vid)]) || t.defaultColor || 'Random Mixed';
    }
    trayVariant(item) {
      if (!this.trayVariants || !window.SvTrayBuilder) return null;
      const z = window.SvTrayBuilder.SIZES[item.size];
      const key = norm(z.name + ' ' + item.stone);
      return this.trayVariants[key + '|' + norm(this.trayColor())] || this.trayVariants[key + '|random mixed'] || null;
    }
    trayPrice(item, where) {
      if (where === 'tray' && (this.cfg.tray.pricing === 'included' || this.cfg.tray.pricing === 'set')) return 0;
      const v = this.trayVariant(item);
      return v && v.available !== false ? v.price : null;
    }
    trayBuilderOpts(extra) {
      const t = this.cfg.tray;
      return Object.assign({
        system: t.system, dp: !!t.dp, extras: t.extras !== false, initial: this.trayState,
        setPrice: t.pricing === 'set' && t.setVariant ? t.setVariant.price : null,
        tint: this.trayTint(),
        priceFor: (it, where) => this.trayPrice(it, where),
        money: (c) => formatMoney(c, this.cfg.moneyFormat)
      }, extra || {});
    }
    whenTrayReady(fn) {
      if (window.SvTrayBuilder) return fn();
      let n = 0;
      const iv = setInterval(() => { if (window.SvTrayBuilder || ++n > 100) { clearInterval(iv); if (window.SvTrayBuilder) fn(); } }, 50);
    }
    renderTrayPreview() {
      const el = this.querySelector('[data-svtb-preview]');
      const btn = this.querySelector('[data-svtb-open]');
      const sum = this.querySelector('[data-svtb-sum]');
      if (!el) return;
      const has = this.trayState.inserts || this.trayState.extras.length;
      if (btn) btn.textContent = has ? '✏️ Edit your tray layout' : '✨ ' + (this.cfg.tray.button || 'Build your tray');
      if (sum) {
        const c = this.trayState.cost || 0;
        if (this.cfg.tray.pricing === 'set' && !this.trayState.extras.length && (!this.trayState.inserts || this.trayState.complete)) {
          sum.textContent = this.trayState.complete ? 'Full set' + (c ? ' · +' + formatMoney(c, this.cfg.moneyFormat) : '') : 'Case only';
        } else sum.textContent = has ? (this.trayState.inserts + ' insert' + (this.trayState.inserts === 1 ? '' : 's') + ' in the tray' +
          (this.trayState.extras.length ? ' + ' + this.trayState.extras.length + ' extra' : '') + (c ? ' · +' + formatMoney(c, this.cfg.moneyFormat) : '')) : '';
      }
      el.hidden = !has;
      if (has) this.whenTrayReady(() => window.SvTrayBuilder.create(el, this.trayBuilderOpts({ readonly: true })));
    }
    openTray() {
      this.whenTrayReady(() => {
        let dlg = this.querySelector('dialog.sv-tb-dialog');
        if (!dlg) {
          dlg = document.createElement('dialog');
          dlg.className = 'sv-tb-dialog';
          dlg.setAttribute('aria-label', this.cfg.tray.title || 'Build your tray');
          dlg.innerHTML = '<div class="sv-tb-dialog__head"><h2 class="sv-tb-dialog__title"></h2>' +
            '<button type="button" class="sv-tb-dialog__x" data-svtb-close aria-label="Close">×</button></div>' +
            '<div class="sv-tb-dialog__body"><div data-svtb-colors-wrap></div><div data-svtb-builder></div></div>' +
            '<div class="sv-tb-dialog__foot"><button type="button" class="button button--primary sv-tb-dialog__done" data-svtb-close>Use this layout</button></div>';
          // keep it inside this element so the quick-add modal's focus trap treats it as "inside"
          this.appendChild(dlg);
          dlg.addEventListener('click', (e) => {
            const col = e.target.closest('[data-svtb-color]');
            if (col) { e.preventDefault(); this.setTrayColor(col.dataset.svtbColor); return; }
            if (e.target.closest('[data-svtb-close]') || e.target === dlg) { e.preventDefault(); dlg.close(); }
          });
          dlg.addEventListener('keyup', (e) => { if (e.key === 'Escape') e.stopPropagation(); });
          dlg.addEventListener('close', () => { this.renderTrayPreview(); this.refresh(); const b = this.querySelector('[data-svtb-open]'); b && b.focus(); });
        }
        const cw = dlg.querySelector('[data-svtb-colors-wrap]'); if (cw) cw.innerHTML = this.trayColorChips();
        dlg.querySelector('.sv-tb-dialog__title').textContent = 'Build your ' + (window.SvTrayBuilder.SYSTEMS[this.cfg.tray.system] || {}).name;
        window.SvTrayBuilder.create(dlg.querySelector('[data-svtb-builder]'), this.trayBuilderOpts({
          onChange: (st) => { this.trayState = st; this.refresh(); this.renderTrayPreview(); }
        }));
        if (typeof dlg.showModal === 'function') dlg.showModal(); else dlg.setAttribute('open', '');
      });
    }

    /* ---------- render ---------- */
    /* ---------- live preview for custom lids & branding medallions (sv-mockup.js) ---------- */
    setupMockup() {
      if (this.cfg.mockup && this.cfg.mockup.off) return;
      if (this.options.some((o) => o.type === 'mockup')) { this._mk = this.mockupSources(this.options.find((o) => o.type === 'mockup')); return; }
      const find = (re, types) => this.options.find((o) => re.test(o.label || '') && (!types || types.includes(o.type)));
      const lid = find(/customi[sz]e your lid/i, ['choice', 'swatch']);
      const isMed = /medallion/i.test((this.cfg.set || '') + ' ' + (this.cfg.setName || '') + ' ' + (this.cfg.productTitle || ''));
      if (!lid && !isMed) return;
      // live previews are parked for now (lids and medallions): on only when the product's seery_us.mockup
      // metafield says {"on": true}, or when the page is opened with ?sv_mockup=1 (for testing)
      if (!((this.cfg.mockup && this.cfg.mockup.on) || /[?&]sv_mockup=1\b/.test(location.search))) return;
      const opt = { id: 'svmock', type: 'mockup', label: 'Live Preview', kind: lid ? 'lid' : 'medallion' };
      if (lid) {
        const show = (lid.values || []).filter((v) => /upload|name|phrase/i.test(v.label)).map((v) => ({ option: lid.id, value: v.label }));
        if (show.length) opt.show_if = show;
      }
      const src = this.mockupSources(opt);
      const idxs = [src.text, src.image, src.font, src.edge, src.edgeChoice, src.center].filter(Boolean).map((o) => this.options.indexOf(o));
      const at = idxs.length ? Math.max.apply(null, idxs) + 1 : this.options.length;
      // keep the preview above any follow-up text box that belongs to the last source
      this.options.splice(at, 0, opt);
      this._mk = src;
    }
    mockupSources(opt) {
      const find = (re, types) => this.options.find((o) => o !== opt && re.test(o.label || '') && (!types || types.includes(o.type)));
      const T = ['text', 'textarea'];
      const s = { opt: opt, kind: opt.kind || 'lid' };
      s.text = find(/name or phrase|engraved name|^name\s*\/\s*text$|desired.*text|personali[sz]ation/i, T);
      s.font = find(/font/i, ['choice', 'swatch']);
      s.image = find(/center design.*upload|upload design|logo.*upload/i, ['file']);
      s.shape = find(/^shape$/i, ['choice', 'swatch']);
      s.center = find(/^center design$/i, ['choice', 'swatch']);
      s.edgeChoice = find(/^edge text$/i, ['choice', 'swatch']);
      s.edge = find(/custom edge text/i, T);
      s.materials = this.options.filter((o) => o.type === 'swatch' && /colou?r/i.test(o.label || ''));
      return s;
    }
    initMockup() {
      this.mockup = null;
      const host = this.body.querySelector('[data-sv-mockup]');
      if (!host || !this._mk || !window.SvMockup) return;
      this.mockup = window.SvMockup.create(host, { kind: this._mk.kind, onChange: () => { this.syncHiddenInputs(); this.queueMockupFile(); } });
    }
    mockupShape() {
      const c = this.cfg.mockup || {};
      if (c.shape || c.w) return c;
      const s = this._mk;
      if (s.kind === 'medallion') {
        const v = s.shape ? ((this.state[s.shape.id] || {}).values || [])[0] : '';
        if (/bar/i.test(this.cfg.productTitle || '')) return { shape: 'rect', w: 3, h: 1, r: 0.2 };
        if (!v || /random/i.test(v)) return { shape: 'circle' };
        if (/custom/i.test(v)) return { shape: 'circle', shapeNote: 'Custom shapes show as a circle in this preview.' };
        return { shape: v };
      }
      const t = this.cfg.productTitle || '';
      const dim = t.match(/(\d+(?:\.\d+)?)\s*["\u201d]?\s*x\s*(\d+(?:\.\d+)?)\s*["\u201d]/i);
      if (dim) return { shape: 'rect', w: Number(dim[1]), h: Number(dim[2]) };
      if (/square up|hefty/i.test(t)) return { shape: 'square' };
      if (/charlene|blossom|round|circle/i.test(t)) return { shape: 'circle' };
      return { shape: 'rect', w: 4, h: 3 };
    }
    mockupBase() {
      const vid = this.currentVariantId();
      const title = (this.cfg.variantTitles || {})[vid] || '';
      const fam = ['Random Mixed', 'Black', 'White', 'Pinks', 'Purples', 'Blues', 'Greens', 'Yellows', 'Oranges', 'Reds', 'Clear'];
      const hit = fam.find((f) => title.toLowerCase().split(/\s*\/\s*/).includes(f.toLowerCase()));
      if (hit) return hit;
      if (this.trayState && this.trayColor) { try { const c = this.trayColor(); if (c) return c; } catch (e) {} }
      return 'Black';
    }
    syncMockup() {
      if (!this.mockup || !this._mk) return;
      const s = this._mk, st = (o) => (o ? this.state[o.id] || {} : {});
      const vis = (o) => o && this.isVisible(o);
      const fileOf = (o) => { const e = o && vis(o) && this.optEl(o.id); const f = e && e.querySelector('[data-sv-file]'); return f && f.files && f.files[0] ? f.files[0] : null; };
      let text = vis(s.text) ? st(s.text).text : '';
      let image = fileOf(s.image);
      if (s.center && vis(s.center)) {
        const cv = (st(s.center).values || [])[0] || '';
        if (/^logo\/svg$/i.test(cv)) text = '';
        if (/name\/text/i.test(cv) && !/logo/i.test(cv)) image = null;
        if (/seery-us choice/i.test(cv)) { text = ''; image = null; }
      }
      let edge = '';
      if (s.edgeChoice && vis(s.edgeChoice)) {
        const ev = (st(s.edgeChoice).values || [])[0] || '';
        if (/custom/i.test(ev)) edge = vis(s.edge) ? st(s.edge).text : '';
        else if (/care/i.test(ev)) edge = 'Tumbler care instructions';
      }
      const mat = s.materials.filter(vis).map((o) => (st(o).values || [])[0]).find(Boolean) || 'Yellow Gold';
      const shape = this.mockupShape();
      this.mockup.update({
        shape: shape.shape, w: shape.w, h: shape.h, r: shape.r, shapeNote: shape.shapeNote,
        material: /random/i.test(mat) ? 'Yellow Gold' : mat, base: this.mockupBase(),
        text: text, font: (st(s.font).values || [])[0] || '', image: image, edge: edge
      });
      this.queueMockupFile();
    }
    queueMockupFile() {
      clearTimeout(this._mkT);
      this._mkT = setTimeout(() => this.writeMockupFile(), 700);
    }
    async writeMockupFile() {
      const form = this.form;
      if (!form || !window.DataTransfer) return;
      let inp = this.querySelector('input[data-sv-mock-file]');
      const opt = this._mk && this._mk.opt;
      const show = this.mockup && opt && this.isVisible(opt) && this.mockup.hasContent();
      if (!show) { if (inp) inp.remove(); return; }
      try {
        const blob = await this.mockup.toBlob();
        const file = new File([blob], 'mockup-preview.png', { type: 'image/png' });
        const dt = new DataTransfer(); dt.items.add(file);
        if (!inp) {
          inp = document.createElement('input');
          inp.type = 'file'; inp.hidden = true; inp.name = 'properties[Mockup Preview]';
          inp.setAttribute('form', form.getAttribute('id')); inp.dataset.svMockFile = '1';
          this.appendChild(inp);
        }
        inp.files = dt.files;
      } catch (e) { console.warn('[SV Options] mockup preview not attached', e); }
    }

    render() {
      const html = [];
      if (this.cfg.vipOnly && !this.cfg.isVip) {
        html.push('<div class="sv-viponly">' +
          '<p class="sv-viponly__note">🔒 <strong>VIP exclusive.</strong> This one is for VIP members and Visionaries.</p>' +
          '<div class="sv-viponly__actions">' +
            '<a class="sv-lockcard__cta" href="' + esc(this.cfg.vipUrl) + '">' +
              esc(this.cfg.vipCta || 'Unlock with VIP') + '<span aria-hidden="true"> →</span></a>' +
            (this.cfg.loggedIn ? '' : '<a class="sv-viponly__signin" href="' + esc(this.cfg.loginUrl) + '">Already a member? Sign in</a>') +
          '</div></div>');
      }
      html.push(this.renderVariantProxy());
      this.options.forEach((opt) => {
        if (opt._inCard) return;
        const aud = this.audienceOk(opt.audience);
        if (aud === 'hide') return;
        html.push(this.renderOption(opt, aud === 'locked'));
      });
      this.body.innerHTML = html.join('');
      this.initMockup();
      this.body.addEventListener('change', (e) => this.onChange(e));
      this.body.addEventListener('input', (e) => {
        if (e.target.matches('[data-sv-text]')) this.onChange(e);
        if (e.target.matches('[data-sv-varqty]') && this.pvSizes[e.target.dataset.svVarqty]) { this.pvSizes[e.target.dataset.svVarqty] = Math.max(1, parseInt(e.target.value, 10) || 1); this.syncVariantProxy(); this.renderTotal(); }
        if (e.target.matches('[data-svtb-qty]')) this.stepTrayQty(0, parseInt(e.target.value, 10) || 1);
      });
      this.body.addEventListener('click', (e) => {
        const tk = e.target.closest('[data-sv-thick]');
        if (tk) { e.preventDefault(); this.openThickness(tk.dataset.svThick); return; }
        const vstep = e.target.closest('[data-sv-varstep]');
        if (vstep) {
          e.preventDefault();
          const inp = vstep.parentElement.querySelector('[data-sv-varqty]');
          inp.value = Math.max(1, (parseInt(inp.value, 10) || 1) + Number(vstep.dataset.svVarstep));
          this.pvSetSize(inp.dataset.svVarqty, true, parseInt(inp.value, 10));
          return;
        }
        const step = e.target.closest('[data-sv-step]');
        if (step) { e.preventDefault(); this.onStep(step); }
        const tog = e.target.closest('[data-sv-toggle]');
        if (tog) { e.preventDefault(); this.toggleCollapse(tog.closest('[data-sv-collapse]')); }
        if (e.target.closest('[data-svtb-open]')) { e.preventDefault(); this.openTray(); }
        const col = e.target.closest('[data-svtb-color]');
        if (col && this.body.contains(col)) { e.preventDefault(); this.setTrayColor(col.dataset.svtbColor); }
        const qb = e.target.closest('[data-svtb-q]');
        if (qb) { e.preventDefault(); this.stepTrayQty(Number(qb.dataset.svtbQ)); }
      });
    }

    renderOption(opt, locked) {
      const id = this.uid + '-' + opt.id;
      const req = opt.required ? '<span class="sv-req" aria-hidden="true">*</span>' : '';
      const help = opt.help ? '<div class="sv-help">' + esc(opt.help) + '</div>' : '';
      const wrapOpen = '<div class="sv-opt sv-opt--' + esc(opt.type) + (locked ? ' sv-opt--locked' : '') + (opt.half ? ' sv-opt--half' : '') +
        '" data-sv-opt="' + esc(opt.id) + '">';
      const thick = /thickness/i.test(opt.label || '') && this.thicknessRows(opt).length ?
        ' <button type="button" class="sv-thick-btn" data-sv-thick="' + esc(opt.id) + '">Compare thicknesses</button>' : '';
      const labelTxt = esc(opt.label) + req + (locked ? ' ' + this.lockHtml() : '') + thick;
      const minmax = (opt.min || opt.max) && !opt.hide_minmax ? '<span class="sv-minmax">' +
        (opt.min && opt.max && opt.min === opt.max ? 'Choose ' + opt.min :
          [opt.min ? 'min ' + opt.min : '', opt.max ? 'max ' + opt.max : ''].filter(Boolean).join(', ')) + '</span>' : '';

      switch (opt.type) {
        case 'tray_builder': {
          const t = this.cfg.tray;
          const setP = t.setVariant ? formatMoney(t.setVariant.price, this.cfg.moneyFormat) : '';
          const note = t.pricing === 'included' ? 'Inserts that fit are included. Extra trays are charged per tray.' :
            (t.pricing === 'set' ? 'Leave it empty for case only, or fill every tray for the full set (+' + setP + ').' : 'Each insert is priced by size.');
          const colors = t.colorOption ? '<div class="sv-tb-block"><div class="sv-label">' + esc(t.colorOption.name) + '</div><div data-svtb-colors-wrap>' + this.trayColorChips() + '</div></div>' : '';
          const cardOpts = this.options.filter((o) => o._inCard && this.audienceOk(o.audience) !== 'hide')
            .map((o) => this.renderOption(o, this.audienceOk(o.audience) === 'locked')).join('');
          return wrapOpen + '<div class="sv-tb-card">' + colors + cardOpts +
            '<div class="sv-tb-block"><div class="sv-label">' + labelTxt + '</div>' +
            '<div class="sv-help sv-tb-note">' + esc(t.note || note) + '</div>' +
            '<button type="button" class="sv-tb-open" data-svtb-open>✨ ' + esc(t.button || 'Build your tray') + '</button>' +
            '<div class="sv-tb-sum" data-svtb-sum></div>' +
            '<div class="sv-tb-preview" data-svtb-preview hidden></div></div></div></div>';
        }
        case 'info':
          return wrapOpen + '<div class="sv-info rte">' + (opt.html || '') + '</div></div>';
        case 'heading':
          return wrapOpen + '<div class="sv-heading">' + (opt.html || esc(opt.label)) + '</div></div>';
        case 'divider':
          return wrapOpen + '<hr class="sv-divider"></div>';
        case 'text':
        case 'email':
        case 'number': {
          const t = opt.type === 'text' ? 'text' : opt.type;
          const pi = this.optionPriceInfo(opt);
          return wrapOpen + '<label class="sv-label" for="' + id + '">' + labelTxt + ' ' + this.priceTag(pi, opt.vip_free) + '</label>' +
            '<input class="sv-input field__input" id="' + id + '" type="' + t + '" data-sv-text' +
            (opt.placeholder ? ' placeholder="' + esc(opt.placeholder) + '"' : '') +
            (opt.maxlength ? ' maxlength="' + Number(opt.maxlength) + '"' : '') +
            (locked ? ' disabled' : '') + '>' + help + '</div>';
        }
        case 'textarea':
          return wrapOpen + '<label class="sv-label" for="' + id + '">' + labelTxt + '</label>' +
            '<textarea class="sv-input sv-textarea field__input" id="' + id + '" rows="3" data-sv-text' +
            (opt.placeholder ? ' placeholder="' + esc(opt.placeholder) + '"' : '') + (locked ? ' disabled' : '') + '></textarea>' + help + '</div>';
        case 'mockup':
          return wrapOpen + '<div class="sv-label">' + esc(opt.label || 'Live Preview') + '</div><div class="sv-mockup" data-sv-mockup></div></div>';
        case 'file': {
          const pi = this.optionPriceInfo(opt);
          return wrapOpen + '<label class="sv-label" for="' + id + '">' + labelTxt + ' ' + this.priceTag(pi, opt.vip_free) + '</label>' +
            '<input class="sv-file" id="' + id + '" type="file" data-sv-file name="' + esc(this.propName(opt)) + '" form="' + esc(this.dataset.form) + '"' +
            (opt.accept ? ' accept="' + esc(opt.accept) + '"' : '') + (locked ? ' disabled' : '') + '>' + help + '</div>';
        }
        case 'swatch':
        case 'choice':
        default:
          if (locked) {
            const n = (opt.values || []).length;
            const noteTxt = opt.lockNote || ((n ? n + ' choices' : 'This option') + ' unlock for VIP members.');
            return wrapOpen + '<div class="sv-label" id="' + id + '-l">' + esc(opt.label) + req + '</div>' +
              '<div class="sv-lockcard">' +
                '<p class="sv-lockcard__note">' + esc(noteTxt) + '</p>' +
                '<a class="sv-lockcard__cta" href="' + esc(this.cfg.vipUrl) + '" target="_blank" rel="noopener">' +
                  esc(this.cfg.vipCta || 'Unlock with VIP') + '<span aria-hidden="true"> →</span></a>' +
              '</div></div>';
          }
          const valsHtml = this.renderValues(opt, id, locked);
          const nVals = (opt.values || []).length;
          const isDrop = opt.display === 'dropdown' && !opt.multi && !opt.qty;
          if (nVals > (this.cfg.collapseOver || 12) && !isDrop) {
            // long lists (colors) stay folded until the shopper opens them
            return wrapOpen + '<div class="sv-label" id="' + id + '-l">' + labelTxt + ' ' + minmax + '</div>' +
              '<div class="sv-collapse" data-sv-collapse>' +
              '<button type="button" class="sv-collapse__btn" data-sv-toggle aria-expanded="false">' +
              '<span class="sv-collapse__pick" data-sv-pick>Choose from ' + nVals + (opt.type === 'swatch' ? ' colors' : ' options') + '</span>' +
              '<span class="sv-collapse__chev" aria-hidden="true">▾</span></button>' +
              '<div class="sv-collapse__body" data-sv-collapse-body hidden>' + valsHtml + '</div></div>' + help + '</div>';
          }
          return wrapOpen + '<div class="sv-label" id="' + id + '-l">' + labelTxt + ' ' + minmax + '</div>' +
            valsHtml + help + '</div>';
      }
    }

    renderValues(opt, id, locked) {
      const vals = (opt.values || []).map((v, i) => Object.assign({ _i: i }, v))
        .filter((v) => this.audienceOk(v.audience) !== 'hide');
      if (opt.display === 'dropdown' && !opt.multi && !opt.qty) {
        const opts = ['<option value="">Choose…</option>'].concat(vals.map((v) => {
          const vlock = this.audienceOk(v.audience) === 'locked';
          const pi = this.valuePriceInfo(v);
          const p = pi && pi.price != null ? ' (+' + formatMoney(pi.price, this.cfg.moneyFormat) + ')' : '';
          return '<option value="' + esc(v.label) + '"' + (vlock ? ' disabled' : '') + '>' + esc(v.label) + esc(p) + (vlock ? ' 🔒 ' + esc(this.cfg.vipLabel) : '') + '</option>';
        }));
        return '<div class="select"><select class="sv-select select__select" id="' + id + '" data-sv-select' + (locked ? ' disabled' : '') + '>' + opts.join('') + '</select></div>';
      }
      const type = opt.multi ? 'checkbox' : 'radio';
      const cls = opt.type === 'swatch' || opt.display === 'swatch' ? 'sv-values sv-values--swatch' :
        (opt.display === 'buttons' ? 'sv-values sv-values--buttons' : 'sv-values sv-values--chips') +
        (!(opt.type === 'swatch' || opt.display === 'swatch') && vals.length > 5 ? ' sv-values--grid2' : '');
      return '<div class="' + cls + '" role="' + (opt.multi ? 'group' : 'radiogroup') + '" aria-labelledby="' + id + '-l">' +
        vals.map((v) => {
          const vid = id + '-' + v._i;
          const vlock = locked || this.audienceOk(v.audience) === 'locked';
          const pi = this.valuePriceInfo(v);
          const vipFree = !!(v.vip_free && v.variant);
          const media = v.image ? '<img src="' + esc(v.image) + '" alt="" loading="lazy" width="44" height="44">' :
            (v.color ? '<span class="sv-dot" style="background:' + esc(v.color) + '"></span>' : '');
          const qty = opt.qty ? '<span class="sv-qty" data-sv-qty-wrap hidden>' +
            '<button type="button" data-sv-step="-1" aria-label="Less">−</button>' +
            '<input type="number" min="1" value="1" data-sv-qty="' + esc(v.label) + '" aria-label="Quantity for ' + esc(v.label) + '">' +
            '<button type="button" data-sv-step="1" aria-label="More">+</button></span>' : '';
          return '<label class="sv-value' + (vlock ? ' sv-value--locked' : '') + '" for="' + vid + '"' + (v.help ? ' title="' + esc(v.help) + '"' : '') + '>' +
            '<input type="' + type + '" id="' + vid + '" name="' + this.uid + '-' + esc(opt.id) + '" value="' + esc(v.label) + '" data-sv-choice' + (vlock ? ' disabled' : '') + '>' +
            '<span class="sv-value__inner">' + media + '<span class="sv-value__text">' + esc(v.label) + '</span>' +
            this.priceTag(pi, vipFree) + (vlock && !locked ? this.lockHtml() : '') + '</span>' + qty + '</label>';
        }).join('') + '</div>';
    }

    /* ---------- state ---------- */
    optById(id) { return this.options.find((o) => o.id === id); }
    optEl(id) { return this.body.querySelector('[data-sv-opt="' + id + '"]'); }

    readOption(opt) {
      const el = this.optEl(opt.id);
      const st = { values: [], qty: {}, text: '', hasFile: false };
      if (!el) return st;
      if (opt.type === 'tray_builder') {
        st.tray = this.trayState;
        if (this.trayState.inserts || this.trayState.extras.length) st.values.push('tray');
        return st;
      }
      if (opt.type === 'file') {
        const f = el.querySelector('[data-sv-file]');
        st.hasFile = !!(f && f.files && f.files.length);
      } else if (['text', 'textarea', 'email', 'number'].includes(opt.type)) {
        const t = el.querySelector('[data-sv-text]');
        st.text = t ? t.value.trim() : '';
      } else {
        const sel = el.querySelector('[data-sv-select]');
        if (sel) { if (sel.value) st.values.push(sel.value); }
        el.querySelectorAll('[data-sv-choice]:checked').forEach((c) => {
          st.values.push(c.value);
          if (opt.qty) {
            const q = c.closest('.sv-value').querySelector('[data-sv-qty]');
            st.qty[c.value] = Math.max(1, parseInt(q && q.value, 10) || 1);
          }
        });
      }
      return st;
    }

    isVisible(opt) {
      if (this.audienceOk(opt.audience) === 'hide') return false;
      if (opt.id === 'thick' && this.pvNativeValues().some((v) => /shaker/i.test(v))) return false;
      if (!opt.show_if || !opt.show_if.length) return true;
      // show when ANY rule matches (OPTIS behaviour). Rules on hidden parents never match.
      return opt.show_if.some((r) => {
        // rule on a Shopify variant option, e.g. {"variant":"Size","value":"1.5\""}: show only when that size is picked
        if (r.variant) {
          const g = (this.pv || []).find((x) => norm(x.name) === norm(r.variant));
          let v = g ? this.pvShown(g) : '';
          if (!g) {
            const picker = this.scopeEl().querySelector('variant-radios, variant-selects');
            if (picker) picker.querySelectorAll('fieldset, .product-form__input').forEach((w) => {
              const lab = w.querySelector('legend, label');
              if (!lab || norm(lab.textContent.trim()) !== norm(r.variant)) return;
              const c = w.querySelector('input[type="radio"]:checked'), sel = w.querySelector('select');
              v = c ? c.value : (sel ? sel.value : '');
            });
          }
          if (norm(r.value) === 'any') return !!v;
          return norm(v) === norm(r.value);
        }
        const parent = this.optById(r.option);
        if (!parent || !this.isVisible(parent)) return false;
        const vals = (this.state[parent.id] || {}).values || [];
        if (norm(r.value) === 'any') return vals.length > 0;
        return vals.some((v) => norm(v) === norm(r.value));
      });
    }

    onChange(e) {
      if (e.target.matches('[data-sv-varchoice]')) { this.pickVariant(Number(e.target.dataset.svVarchoice), e.target.value); return; }
      if (e.target.matches('[data-sv-varselect]')) { this.pickVariant(Number(e.target.dataset.svVarselect), e.target.value); return; }
      if (e.target.matches('[data-sv-varsize]')) { this.pvSetSize(e.target.value, e.target.checked); return; }
      if (e.target.matches('[data-sv-varqty]')) { if (this.pvSizes[e.target.dataset.svVarqty]) this.pvSetSize(e.target.dataset.svVarqty, true, parseInt(e.target.value, 10) || 1); return; }
      const optEl = e.target.closest('[data-sv-opt]');
      if (optEl && e.target.matches('[data-sv-choice]')) {
        const opt = this.optById(optEl.dataset.svOpt);
        if (opt && opt._one && e.target.checked) {
          optEl.querySelectorAll('[data-sv-choice]:checked').forEach((c) => { if (c !== e.target) c.checked = false; });
        }
        if (opt && opt.multi && opt.max) {
          const checked = optEl.querySelectorAll('[data-sv-choice]:checked');
          if (checked.length > opt.max) { e.target.checked = false; this.flash(optEl, 'You can choose up to ' + opt.max + '.'); }
        }
        const lab = e.target.closest('.sv-value');
        const qw = lab && lab.querySelector('[data-sv-qty-wrap]');
        if (qw) qw.hidden = !e.target.checked;
        if (opt && !opt.multi) optEl.querySelectorAll('[data-sv-qty-wrap]').forEach((w) => {
          const c = w.closest('.sv-value').querySelector('[data-sv-choice]'); w.hidden = !c.checked;
        });
      }
      if (optEl) optEl.classList.remove('sv-opt--error');
      if (optEl && e.target.matches('[data-sv-choice]') && e.target.type === 'radio') {
        const c = optEl.querySelector('[data-sv-collapse]');
        if (c) this.toggleCollapse(c, false);
      }
      this.refresh();
      this.saveMemory();
    }

    onStep(btn) {
      const input = btn.parentElement.querySelector('[data-sv-qty]');
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(btn.dataset.svStep));
      this.refresh();
    }

    toggleCollapse(c, open) {
      if (!c) return;
      const body = c.querySelector('[data-sv-collapse-body]'), btn = c.querySelector('[data-sv-toggle]');
      const show = open == null ? body.hidden : open;
      body.hidden = !show;
      btn.setAttribute('aria-expanded', String(show));
      c.classList.toggle('is-open', show);
    }
    syncCollapseLabels() {
      this.body.querySelectorAll('[data-sv-collapse]').forEach((c) => {
        const pick = c.querySelector('[data-sv-pick]');
        const checked = Array.from(c.querySelectorAll('[data-sv-choice]:checked'));
        if (!pick.dataset.empty) pick.dataset.empty = pick.textContent;
        if (!checked.length) { pick.innerHTML = esc(pick.dataset.empty); c.classList.remove('has-pick'); return; }
        c.classList.add('has-pick');
        if (checked.length > 3) { pick.textContent = checked.length + ' selected'; return; }
        pick.innerHTML = checked.map((ch) => {
          const lab = ch.closest('.sv-value');
          const img = lab && lab.querySelector('img');
          const dot = lab && lab.querySelector('.sv-dot');
          return (img ? '<img src="' + esc(img.getAttribute('src')) + '" alt="" width="28" height="28">' : (dot ? dot.outerHTML : '')) + '<span>' + esc(ch.value) + '</span>';
        }).join('') + '<span class="sv-collapse__change">Change</span>';
      });
    }

    flash(el, msg) {
      let n = el.querySelector('.sv-flash');
      if (!n) { n = document.createElement('div'); n.className = 'sv-flash'; el.appendChild(n); }
      n.textContent = msg;
      clearTimeout(n._t); n._t = setTimeout(() => n.remove(), 2500);
    }

    refresh() {
      this.options.forEach((opt) => { this.state[opt.id] = this.readOption(opt); });
      this.options.forEach((opt) => {
        const el = this.optEl(opt.id);
        if (!el) return;
        const vis = this.isVisible(opt);
        el.hidden = !vis;
        el.querySelectorAll('input,select,textarea').forEach((i) => {
          if (i.closest('.sv-value--locked') || el.classList.contains('sv-opt--locked')) return;
          i.disabled = !vis;
        });
      });
      // re-read after visibility (hidden parents clear children)
      this.options.forEach((opt) => { this.state[opt.id] = this.readOption(opt); });
      this.syncMockup();
      this.syncHiddenInputs();
      this.syncCollapseLabels();
      this.syncVariantProxy();
      this.renderTotal();
    }

    /* line item properties (hidden inputs bound to the product form) */
    syncHiddenInputs() {
      const form = this.form;
      if (!form) return;
      this.querySelectorAll('input[data-sv-prop]').forEach((i) => i.remove());
      const add = (name, value) => {
        const i = document.createElement('input');
        i.type = 'hidden'; i.name = name; i.value = value;
        i.setAttribute('form', form.getAttribute('id')); i.dataset.svProp = '1';
        this.appendChild(i);
      };
      this.properties().forEach(([k, v]) => add('properties[' + k + ']', v));
      if (this.cfg.set) add('properties[_sv_set]', this.cfg.set);
    }

    properties() {
      const props = [];
      this.options.forEach((opt) => {
        if (!this.isVisible(opt)) return;
        if (['info', 'heading', 'divider', 'file', 'mockup'].includes(opt.type)) {
          if (opt.type === 'mockup' && this.mockup && this.mockup.hasContent()) {
            const ink = this.mockup.inkName();
            if (ink) props.push(['Preview Design Color', ink]);
            props.push(['_Mockup Settings', this.mockup.summary()]);
          }
          return;
        }
        const st = this.state[opt.id];
        if (opt.type === 'tray_builder') {
          if (this.trayState.inserts) props.push(['Tray Layout', this.trayState.layout]);
          if (this.trayState.extras.length) props.push(['Extra Trays', this.trayState.extrasText]);
          return;
        }
        let val = '';
        if (st.text) val = st.text;
        else if (st.values.length) {
          val = st.values.map((v) => (opt.qty ? (st.qty[v] || 1) + ' x ' + v : v)).join(', ');
        }
        if (val) props.push([opt.label, val]);
      });
      return props;
    }

    /* paid add-ons for the current selections: [{ id, qty, label }] (qty is per 1 product) */
    addons() {
      const list = [];
      const push = (info, qty, label) => {
        if (!info || !info.variant) return;
        const found = list.find((a) => String(a.id) === String(info.variant));
        if (found) { found.qty += qty; if (found.label.split('; ').indexOf(label) < 0) found.label += '; ' + label; } else list.push({ id: Number(info.variant), qty: qty, label: label, price: info.price });
      };
      this.options.forEach((opt) => {
        if (!this.isVisible(opt)) return;
        const st = this.state[opt.id];
        if (opt.type === 'tray_builder') {
          const add = (it, where, label) => {
            if (this.trayPrice(it, where) === 0) return; // included in the tray price
            const v = this.trayVariant(it);
            const z = window.SvTrayBuilder ? window.SvTrayBuilder.SIZES[it.size].name : it.size;
            if (v) push({ variant: v.id, price: v.price }, 1, label + ': ' + z + ' ' + it.stone);
            else list.push({ id: 0, qty: 1, label: label + ': ' + z + ' ' + it.stone, price: 0, missing: true });
          };
          (this.trayState.rows || []).forEach((r) => r.forEach((it) => add(it, 'tray', 'Tray insert')));
          const t = this.cfg.tray;
          if (t.pricing === 'set' && t.setVariant && this.trayState.complete) push({ variant: t.setVariant.id, price: t.setVariant.price }, 1, 'Full tray set');
          (this.trayState.extras || []).forEach((it) => add(it, 'extra', 'Extra tray'));
          return;
        }
        if ((st.text || st.hasFile) && opt.variant) push(this.optionPriceInfo(opt), 1, opt.label);
        (opt.values || []).forEach((v) => {
          if (!st.values.some((s) => norm(s) === norm(v.label))) return;
          push(this.valuePriceInfo(v), opt.qty ? (st.qty[v.label] || 1) : 1, opt.label + ': ' + v.label);
        });
      });
      return list;
    }

    currentVariantId() {
      const f = this.form;
      const i = f && f.querySelector('[name="id"]');
      return i ? i.value : null;
    }
    currentQty() {
      const f = this.form;
      if (!f) return 1;
      const q = document.querySelector('input[name="quantity"][form="' + f.getAttribute('id') + '"]') || f.querySelector('input[name="quantity"]');
      return Math.max(1, parseInt(q && q.value, 10) || 1);
    }

    renderTotal() {
      if (!this.cfg.showTotal) return;
      const addons = this.addons();
      if (this.pvMultiPos != null) {
        const items = this.pvSizeItems().filter((i) => i.variantId);
        const per = addons.reduce((s, a) => s + (a.price || 0) * a.qty, 0);
        const units = items.reduce((s, i) => s + i.qty, 0);
        if (!items.length || (units < 2 && !per)) { this.totalEl.hidden = true; return; }
        const f = (c) => formatMoney(c, this.cfg.moneyFormat);
        const total = items.reduce((s, i) => s + (i.price + per) * i.qty, 0);
        this.totalEl.hidden = false;
        this.totalEl.innerHTML = items.map((i) => '<div class="sv-total__row"><span>' + esc(i.value) + ' × ' + i.qty + '</span><span>' + f(i.price * i.qty) + '</span></div>').join('') +
          addons.map((a) => '<div class="sv-total__row sv-total__row--addon"><span>+ ' + esc(a.label) + ' (each)' + '</span><span>' + f((a.price || 0) * a.qty * units) + '</span></div>').join('') +
          '<div class="sv-total__row sv-total__row--sum"><span>Total (' + units + ' pcs)</span><span>' + f(total) + '</span></div>';
        return;
      }
      const vid = this.currentVariantId();
      const base = vid && this.cfg.variants ? this.cfg.variants[String(vid)] : null;
      if (base == null) { this.totalEl.hidden = true; return; }
      const extra = addons.reduce((s, a) => s + (a.price || 0) * a.qty, 0);
      if (!extra) { this.totalEl.hidden = true; return; }
      const qty = this.currentQty();
      const total = (base + extra) * qty;
      this.totalEl.hidden = false;
      this.totalEl.innerHTML =
        '<div class="sv-total__row"><span>' + esc(this.cfg.productTitle) + '</span><span>' + formatMoney(base, this.cfg.moneyFormat) + '</span></div>' +
        addons.map((a) => '<div class="sv-total__row sv-total__row--addon"><span>+ ' + esc(a.label) + (a.qty > 1 ? ' × ' + a.qty : '') +
          '</span><span>' + formatMoney((a.price || 0) * a.qty, this.cfg.moneyFormat) + '</span></div>').join('') +
        '<div class="sv-total__row sv-total__row--sum"><span>Total' + (qty > 1 ? ' (× ' + qty + ')' : '') + '</span><span>' +
        formatMoney(total, this.cfg.moneyFormat) + '</span></div>';
    }

    bindVariantWatch() {
      const scope = this.closest('.product__info-container, .quick-add-modal__content-info, section') || document;
      scope.addEventListener('change', (e) => {
        if (e.target.closest('variant-radios, variant-selects, quantity-input') || e.target.name === 'quantity') {
          this.refresh();
          setTimeout(() => this.renderTotal(), 50);
        }
      });
      scope.addEventListener('click', (e) => {
        if (e.target.closest('quantity-input button')) setTimeout(() => this.renderTotal(), 50);
      });
    }

    /* ---------- validation ---------- */
    validate() {
      this.refresh();
      const problems = [];
      if (this.cfg.vipOnly && !this.cfg.isVip) problems.push('This item is for VIP members only.');
      if (this.pvMultiPos != null) {
        const items = this.pvSizeItems();
        if (!items.length) problems.push('Please choose at least one size.');
        items.forEach((i) => { if (!i.variantId) problems.push(i.value + (i.soldOut ? ' is sold out' : ' isn\u2019t available') + ' in this finish/color.'); });
      }
      Object.keys(this.pvUnset || {}).forEach((p) => {
        const g = (this.pv || []).find((x) => String(x.pos) === p);
        problems.push('Please choose a ' + (g ? g.name.trim().toLowerCase() : 'size') + '.');
      });
      this.options.forEach((opt) => {
        const el = this.optEl(opt.id);
        if (!el || !this.isVisible(opt)) return;
        if (el.classList.contains('sv-opt--locked')) return;
        const st = this.state[opt.id];
        const filled = st.text || st.hasFile || st.values.length;
        let msg = '';
        if (opt.type === 'tray_builder' && opt.required && !this.trayState.inserts) msg = 'Please build your tray — add at least one insert.';
        else if (opt.type === 'tray_builder' && this.cfg.tray.pricing === 'set' && this.trayState.inserts && !this.trayState.complete)
          msg = 'Fill every tray for the full set, or clear them to order the case only.';
        else if (opt.required && !filled) msg = 'Please complete “' + opt.label + '”.';
        else if (st.values.length && opt.min && st.values.length < opt.min) msg = '“' + opt.label + '”: choose at least ' + opt.min + '.';
        else if (st.values.length && opt.max && st.values.length > opt.max) msg = '“' + opt.label + '”: choose no more than ' + opt.max + '.';
        else if (opt.type === 'email' && st.text && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(st.text)) msg = 'Please enter a valid email for “' + opt.label + '”.';
        el.classList.toggle('sv-opt--error', !!msg);
        if (msg) problems.push(msg);
      });
      // unavailable add-ons
      this.addons().forEach((a) => {
        if (a.missing) { problems.push('“' + a.label + '” isn’t available in this color right now.'); return; }
        const info = this.cfg.addons && this.cfg.addons[String(a.id)];
        if (info && info.available === false) problems.push('“' + a.label + '” is sold out right now.');
      });
      if (problems.length) {
        this.errorEl.hidden = false;
        this.errorEl.innerHTML = problems.map(esc).join('<br>');
        const first = this.body.querySelector('.sv-opt--error') || this.errorEl;
        first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
      this.errorEl.hidden = true;
      return true;
    }

    lockPurchase() {
      const f = this.form;
      if (!f) return;
      const btn = f.querySelector('[type="submit"][name="add"]');
      if (btn) { btn.disabled = true; btn.setAttribute('aria-disabled', 'true'); btn.classList.add('sv-btn-locked'); btn.querySelector('span') && (btn.querySelector('span').textContent = '🔒 VIP only'); }
      f.querySelectorAll('.shopify-payment-button').forEach((b) => (b.style.display = 'none'));
    }

    /* ---------- remember choices ---------- */
    saveMemory() {
      if (!this.cfg.remember) return;
      const mem = readMemory();
      this.options.forEach((opt) => {
        if (!['choice', 'swatch'].includes(opt.type) || opt.qty) return;
        const st = this.state[opt.id];
        if (st && st.values.length === 1) mem[norm(opt.label)] = st.values[0];
      });
      writeMemory(mem);
    }
    applyDefaults() {
      this.options.forEach((opt) => {
        if (!opt.default) return;
        const el = this.optEl(opt.id);
        if (!el || el.querySelector('[data-sv-choice]:checked')) return;
        el.querySelectorAll('[data-sv-choice]').forEach((c) => { if (norm(c.value) === norm(opt.default) && !c.disabled) c.checked = true; });
      });
    }
    applyMemory() {
      if (!this.cfg.remember) return;
      const mem = readMemory();
      this.options.forEach((opt) => {
        if (!['choice', 'swatch'].includes(opt.type) || opt.qty || opt.multi) return;
        const want = mem[norm(opt.label)];
        if (!want) return;
        // never auto-pick a value that costs money
        const v = (opt.values || []).find((x) => norm(x.label) === norm(want));
        if (!v || this.valuePriceInfo(v) || this.audienceOk(v.audience) !== 'show') return;
        const el = this.optEl(opt.id);
        if (!el) return;
        const sel = el.querySelector('[data-sv-select]');
        if (sel) { sel.value = v.label; return; }
        el.querySelectorAll('[data-sv-choice]').forEach((c) => { if (norm(c.value) === norm(v.label) && !c.disabled) c.checked = true; });
      });
    }
  }

  customElements.define('sv-options', SvOptionsElement);

  /* ---------- add to cart with nested add-ons ---------- */
  window.SvOptions = {
    forForm(form) {
      const fid = form && form.getAttribute('id');
      if (!fid) return null;
      return document.querySelector('sv-options[data-form="' + CSS.escape(fid) + '"]');
    },

    /**
     * Adds the product (multipart, so file uploads work) and then its paid add-ons
     * as nested lines. Resolves with a response shaped like /cart/add.js so the
     * theme's cart notification can render it.
     */
    async add(form, svEl, sectionsToRender) {
      const fd = new FormData(form);
      const addons = (svEl ? svEl.addons() : []).filter((a) => a.id);
      // acrylic listings: several sizes of one design → one line per size
      const sizes = svEl && svEl.pvMultiPos != null ? svEl.pvSizeItems().filter((i) => i.variantId) : [];
      if (sizes.length) { fd.set('id', String(sizes[0].variantId)); fd.set('quantity', String(sizes[0].qty)); }
      const qty = sizes.length ? sizes[0].qty : Math.max(1, parseInt(fd.get('quantity'), 10) || 1);
      const extras = sizes.slice(1);
      const sections = (sectionsToRender || []).join(',');
      const withSections = (payload) => { if (sections) { payload.sections = sections; payload.sections_url = window.location.pathname; } return payload; };
      if (!addons.length && !extras.length && sections) { fd.append('sections', sections); fd.append('sections_url', window.location.pathname); }
      const headers = { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' };
      const post = (payload) => fetch(window.routes.cart_add_url, {
        method: 'POST', headers: Object.assign({ 'Content-Type': 'application/json' }, headers), body: JSON.stringify(payload)
      }).then((r) => r.json());
      const rollback = async (keys) => {
        for (const k of keys) {
          await fetch(window.routes.cart_change_url, {
            method: 'POST', headers: Object.assign({ 'Content-Type': 'application/json' }, headers), body: JSON.stringify({ id: k, quantity: 0 })
          });
        }
      };
      const r1 = await fetch(window.routes.cart_add_url, { method: 'POST', headers, body: fd }).then((r) => r.json());
      if (r1.status || (!addons.length && !extras.length)) return r1;

      const lines = [{ key: r1.key, qty }];
      let last = null;
      if (extras.length) {
        const props = Object.assign({}, r1.properties || {});
        const p = { items: extras.map((x) => ({ id: x.variantId, quantity: x.qty, properties: props })) };
        const r = await post(addons.length ? p : withSections(p));
        if (r.status) {
          await rollback([r1.key]);
          return { status: 422, description: 'Sorry — one of the sizes you picked is unavailable right now. ' + (r.description || '') };
        }
        (r.items || []).forEach((it, i) => lines.push({ key: it.key, qty: extras[i] ? extras[i].qty : it.quantity }));
        last = r;
      }
      if (addons.length) {
        const mk = (nested) => withSections({
          items: [].concat.apply([], lines.map((l) => addons.map((a) => Object.assign({
            id: a.id,
            quantity: a.qty * l.qty,
            properties: { 'For': (svEl.cfg.productTitle || ''), 'Option': a.label, '_sv_per_unit': String(a.qty) }
          }, nested ? { parent_line_key: l.key } : {}))))
        });
        let r2 = await post(mk(true));
        if (r2.status) {
          // Parent can't have nested lines (e.g. Simple Bundles kits) — add add-ons as normal lines instead.
          r2 = await post(mk(false));
          if (r2.status) {
            // roll back so the shopper isn't left with an un-paid customization
            await rollback(lines.map((l) => l.key));
            return { status: 422, description: 'Sorry — one of the add-ons you picked is unavailable right now. ' + (r2.description || '') };
          }
        }
        last = r2;
      }
      return Object.assign({}, r1, { sections: last && last.sections });
    },

    /* Keep add-on quantities in step with their product (cart page). Returns true if it changed anything. */
    async syncCart() {
      try {
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const byKey = {}; cart.items.forEach((i) => { byKey[i.key] = i; });
        const updates = {};
        cart.items.forEach((i) => {
          const per = i.properties && i.properties._sv_per_unit;
          const parentKey = i.parent_relationship && i.parent_relationship.parent_key;
          if (!per || !parentKey || !byKey[parentKey]) return;
          const want = byKey[parentKey].quantity * (parseInt(per, 10) || 1);
          if (want !== i.quantity) updates[i.key] = want;
        });
        if (!Object.keys(updates).length) return false;
        await fetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates })
        });
        return true;
      } catch (e) { return false; }
    }
  };
})();
